/**
 * Google Analytics 4 (GA4) Integration
 *
 * Provides GDPR-compliant analytics tracking with:
 * - Consent management
 * - E-commerce event tracking
 * - Custom event tracking
 * - Page view tracking for SPA
 */

import { env } from './env';

interface EcommerceItem {
    item_id: string;
    item_name: string;
    price?: number;
    quantity?: number;
    item_category?: string;
    item_brand?: string;
}

type ConsentStatus = 'granted' | 'denied' | 'pending';

// ========================================
// Consent Management
// ========================================

/**
 * Get current consent status from localStorage
 */
function getConsentStatus(): ConsentStatus {
    if (typeof localStorage === 'undefined') return 'pending';
    const consent = localStorage.getItem('analytics_consent');
    return (consent as ConsentStatus) || 'pending';
}

/**
 * Check if analytics is available and consent given
 */
function isAnalyticsEnabled(): boolean {
    return !!(
        env.GA_MEASUREMENT_ID &&
        typeof window !== 'undefined' &&
        typeof window.gtag === 'function' &&
        getConsentStatus() === 'granted'
    );
}

/**
 * Set analytics consent status
 */
export function setAnalyticsConsent(granted: boolean): void {
    const status: ConsentStatus = granted ? 'granted' : 'denied';
    localStorage.setItem('analytics_consent', status);

    if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
            analytics_storage: status,
        });
    }

    // If consent granted and GA is configured, initialize
    if (granted && env.GA_MEASUREMENT_ID) {
        loadGAScript();
    }
}

/**
 * Check if user has given consent
 */
export function hasAnalyticsConsent(): boolean {
    return getConsentStatus() === 'granted';
}

/**
 * Check if consent is still pending
 */
export function isConsentPending(): boolean {
    return getConsentStatus() === 'pending';
}

// ========================================
// Initialization
// ========================================

/**
 * Load GA script dynamically
 */
function loadGAScript(): void {
    if (!env.GA_MEASUREMENT_ID) return;
    if (document.querySelector(`script[src*="googletagmanager"]`)) return;

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${env.GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    // Initialize dataLayer and gtag
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag(...args: unknown[]) {
        window.dataLayer.push(args);
    };

    window.gtag('js', new Date());
    window.gtag('config', env.GA_MEASUREMENT_ID, {
        anonymize_ip: true,
        send_page_view: false, // We'll send manually for SPA
    });

    console.log('[Analytics] GA4 initialized:', env.GA_MEASUREMENT_ID);
}

/**
 * Initialize Google Analytics
 * Call this on app startup
 */
export function initializeAnalytics(): void {
    if (!env.GA_MEASUREMENT_ID) {
        console.log('[Analytics] No measurement ID configured');
        return;
    }

    // Check consent
    if (getConsentStatus() !== 'granted') {
        console.log('[Analytics] Waiting for consent');
        return;
    }

    loadGAScript();
}

// ========================================
// Page Tracking
// ========================================

/**
 * Track page view (for SPA navigation)
 */
export function trackPageView(path: string, title?: string): void {
    if (!isAnalyticsEnabled()) return;

    window.gtag('event', 'page_view', {
        page_path: path,
        page_title: title || document.title,
        page_location: window.location.href,
    });
}

// ========================================
// Event Tracking
// ========================================

/**
 * Track custom event
 */
export function trackEvent(
    eventName: string,
    params?: Record<string, unknown>
): void {
    if (!isAnalyticsEnabled()) return;

    window.gtag('event', eventName, params);
}

// ========================================
// E-commerce Events (GA4 Enhanced)
// ========================================

/**
 * Track product view
 */
export function trackViewItem(item: EcommerceItem): void {
    trackEvent('view_item', {
        currency: 'EUR',
        value: item.price || 0,
        items: [item],
    });
}

/**
 * Track product list view
 */
export function trackViewItemList(
    items: EcommerceItem[],
    listName: string
): void {
    trackEvent('view_item_list', {
        item_list_name: listName,
        items,
    });
}

/**
 * Track add to cart
 */
export function trackAddToCart(item: EcommerceItem, value: number): void {
    trackEvent('add_to_cart', {
        currency: 'EUR',
        value,
        items: [item],
    });
}

/**
 * Track remove from cart
 */
export function trackRemoveFromCart(item: EcommerceItem, value: number): void {
    trackEvent('remove_from_cart', {
        currency: 'EUR',
        value,
        items: [item],
    });
}

/**
 * Track begin checkout
 */
export function trackBeginCheckout(items: EcommerceItem[], value: number): void {
    trackEvent('begin_checkout', {
        currency: 'EUR',
        value,
        items,
    });
}

/**
 * Track add payment info
 */
export function trackAddPaymentInfo(paymentType: string, value: number): void {
    trackEvent('add_payment_info', {
        currency: 'EUR',
        value,
        payment_type: paymentType,
    });
}

/**
 * Track purchase
 */
const reportedPurchases = new Set<string>();

export function trackPurchase(
    transactionId: string,
    value: number,
    items: EcommerceItem[],
    currency: string = 'EUR'
): void {
    if (!isAnalyticsEnabled() || !transactionId) return;
    const key = `ga4-purchase:${transactionId}`;
    if (reportedPurchases.has(transactionId)) return;
    try {
        if (sessionStorage.getItem(key)) return;
    } catch { /* Storage may be unavailable; the in-memory guard still applies. */ }
    trackEvent('purchase', {
        transaction_id: transactionId,
        currency: currency.toUpperCase(),
        value,
        items,
    });
    reportedPurchases.add(transactionId);
    try { sessionStorage.setItem(key, 'sent'); } catch { /* Best-effort reload guard. */ }
}

// ========================================
// User Events
// ========================================

/**
 * Track user sign up
 */
export function trackSignUp(method: string = 'email'): void {
    trackEvent('sign_up', { method });
}

/**
 * Track user login
 */
export function trackLogin(method: string = 'email'): void {
    trackEvent('login', { method });
}

/**
 * Track search
 */
export function trackSearch(searchTerm: string): void {
    trackEvent('search', { search_term: searchTerm });
}

// ========================================
// Custom Business Events
// ========================================

/**
 * Track date selection
 */
export function trackDateSelection(
    startDate: string,
    endDate: string,
    durationDays: number
): void {
    trackEvent('select_dates', {
        start_date: startDate,
        end_date: endDate,
        duration_days: durationDays,
    });
}

/**
 * Track location selection
 */
export function trackLocationSelection(
    locationId: string,
    locationName: string
): void {
    trackEvent('select_location', {
        location_id: locationId,
        location_name: locationName,
    });
}

/**
 * Track accessory addition
 */
export function trackAccessoryAdded(
    accessoryId: string,
    accessoryName: string,
    price: number
): void {
    trackEvent('add_accessory', {
        accessory_id: accessoryId,
        accessory_name: accessoryName,
        price,
    });
}

/**
 * Track form submission
 */
export function trackFormSubmit(formName: string, success: boolean): void {
    trackEvent('form_submit', {
        form_name: formName,
        success,
    });
}

/**
 * Track error occurrence (for analytics, not error tracking)
 */
export function trackError(errorType: string, errorMessage: string): void {
    trackEvent('error', {
        error_type: errorType,
        error_message: errorMessage.substring(0, 100), // Limit length
    });
}
